import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { differenceInSeconds, format } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, Play, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { cn } from '~/lib/utils';

type LatestRunData = NonNullable<ReturnType<typeof trpc.bookabilityCheck.getLatestRun.useQuery>['data']>;
type ResultRow = LatestRunData['results'][number];
type ResultStatus = ResultRow['status'];

const STATUS_LABELS: Record<ResultStatus, string> = {
    NONE: 'Nicht buchbar',
    ERROR: 'Fehler',
    LOW: 'Kaum buchbar',
    OK: 'OK',
};

const STATUS_CLASSNAMES: Record<ResultStatus, string> = {
    NONE: 'bg-red-100 text-red-800 border-red-200',
    ERROR: 'bg-orange-100 text-orange-800 border-orange-200',
    LOW: 'bg-amber-100 text-amber-800 border-amber-200',
    OK: 'bg-green-100 text-green-800 border-green-200',
};

const ISSUE_LABELS: Record<string, string> = {
    VARIANT_ARCHIVED: 'Variante oder Leistung archiviert',
    NO_STEPS: 'Variante hat keine Behandlungsschritte',
    EQUIPMENT_DEFECT: 'Benötigtes Gerät ist als defekt markiert',
    NO_EQUIPMENT: 'Benötigtes Gerät fehlt am Standort',
    NO_ROOM: 'Benötigter Raum fehlt am Standort',
    NO_PRACTITIONER: 'Kein:e Behandler:in am Standort darf die Variante durchführen',
    VARIANT_NOT_AT_STORE: 'Variante ist an diesem Standort nicht angeboten',
};

const TRIGGER_LABELS: Record<string, string> = {
    CRON: 'automatisch',
    MANUAL: 'manuell',
};

const formatDateTime = (iso: string) => format(new Date(iso), 'dd.MM.yyyy HH:mm', { locale: de });

const formatDuration = (fromIso: string, toIso: string) => {
    const seconds = differenceInSeconds(new Date(toIso), new Date(fromIso));
    const minutes = Math.floor(seconds / 60);
    return minutes > 0 ? `${minutes} min ${seconds % 60} s` : `${seconds} s`;
};

const SortableHeader = ({ label, column }: { label: string; column: { getIsSorted: () => false | 'asc' | 'desc'; toggleSorting: (desc?: boolean) => void; clearSorting: () => void } }) => {
    const sorted = column.getIsSorted();
    return (
        <Button
            variant='ghost'
            className='-ml-3'
            onClick={() => {
                if (sorted === 'asc') column.toggleSorting(true);
                else if (sorted === 'desc') column.clearSorting();
                else column.toggleSorting(false);
            }}
        >
            {label}
            {sorted === 'asc' ? <ArrowUp /> : sorted === 'desc' ? <ArrowDown /> : <ArrowUpDown className='opacity-50' />}
        </Button>
    );
};

const buildColumns = (horizonDays: number): ColumnDef<ResultRow>[] => [
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
            <Badge variant='outline' className={STATUS_CLASSNAMES[row.original.status]}>
                {STATUS_LABELS[row.original.status]}
            </Badge>
        ),
    },
    {
        accessorKey: 'bookableItemName',
        header: ({ column }) => <SortableHeader label='Bookable Item' column={column} />,
        cell: ({ row }) => <span className='font-medium'>{row.original.bookableItemName}</span>,
    },
    {
        accessorKey: 'optionLabel',
        header: 'Option',
    },
    {
        accessorKey: 'storeName',
        header: ({ column }) => <SortableHeader label='Standort' column={column} />,
    },
    {
        accessorKey: 'bookableDays',
        header: ({ column }) => <SortableHeader label='Buchbare Tage' column={column} />,
        cell: ({ row }) => `${row.original.bookableDays} / ${horizonDays}`,
    },
    {
        accessorKey: 'slotCount',
        header: ({ column }) => <SortableHeader label='Slots' column={column} />,
    },
    {
        id: 'practitioners',
        accessorFn: (row) => row.practitioners.length,
        header: ({ column }) => <SortableHeader label='Behandler' column={column} />,
        cell: ({ row }) => {
            const { practitioners } = row.original;
            if (practitioners.length === 0) return '–';
            return (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className='cursor-help underline decoration-dotted underline-offset-4'>{practitioners.length}</span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <ul>
                            {practitioners.map((practitioner) => <li key={practitioner.id}>{practitioner.name}</li>)}
                        </ul>
                    </TooltipContent>
                </Tooltip>
            );
        },
    },
    {
        accessorKey: 'firstSlotAt',
        header: 'Erster freier Slot',
        cell: ({ row }) => row.original.firstSlotAt ? formatDateTime(row.original.firstSlotAt) : '–',
    },
    {
        id: 'issues',
        header: 'Hinweise',
        cell: ({ row }) => {
            const { issues, error, status } = row.original;
            if (error) {
                return <span className='text-xs text-orange-700'>{error}</span>;
            }
            if (issues.length > 0) {
                return (
                    <ul className='list-disc pl-4 text-xs'>
                        {issues.map((issue) => <li key={issue}>{ISSUE_LABELS[issue] ?? issue}</li>)}
                    </ul>
                );
            }
            if (status === 'NONE') {
                return <span className='text-xs text-muted-foreground'>Ressourcen vorhanden: ausgebucht oder keine Schichten</span>;
            }
            return null;
        },
    },
];

const BookabilityCheck = () => {
    const [storeFilter, setStoreFilter] = useState<string>('all');
    const [onlyProblems, setOnlyProblems] = useState<boolean>(true);
    const [search, setSearch] = useState<string>('');
    const [sorting, setSorting] = useState<SortingState>([]);

    const utils = trpc.useUtils();
    const latestRunQuery = trpc.bookabilityCheck.getLatestRun.useQuery(undefined, {
        refetchInterval: (query) => query.state.data?.latestRun?.status === 'RUNNING' ? 5000 : false,
        refetchOnWindowFocus: false,
    });

    const startRunMutation = trpc.bookabilityCheck.startRun.useMutation({
        onSuccess: () => {
            toast.success('Buchbarkeits-Check gestartet', { description: 'Das Ergebnis erscheint automatisch, sobald der Check fertig ist.' });
            utils.bookabilityCheck.getLatestRun.invalidate();
        },
        onError: (error) => {
            toast.error('Check konnte nicht gestartet werden', { description: error.message });
        },
    });

    const data = latestRunQuery.data;
    const latestRun = data?.latestRun ?? null;
    const resultsRun = data?.resultsRun ?? null;
    const horizonDays = data?.horizonDays ?? 14;
    const isRunning = latestRun?.status === 'RUNNING';

    const allResults = data?.results ?? [];

    const storeOptions = useMemo(() => {
        const names = new Map<string, string>();
        for (const result of allResults) {
            names.set(String(result.storeId ?? 'digital'), result.storeName);
        }
        return [...names.entries()].sort((a, b) => a[1].localeCompare(b[1], 'de'));
    }, [allResults]);

    const filteredResults = useMemo(() => {
        const searchLower = search.trim().toLowerCase();
        return allResults.filter((result) =>
            (storeFilter === 'all' || String(result.storeId ?? 'digital') === storeFilter)
            && (!onlyProblems || result.status !== 'OK')
            && (!searchLower || result.bookableItemName.toLowerCase().includes(searchLower) || result.optionLabel.toLowerCase().includes(searchLower))
        );
    }, [allResults, storeFilter, onlyProblems, search]);

    const counts = useMemo(() => {
        const byStatus: Record<ResultStatus, number> = { NONE: 0, ERROR: 0, LOW: 0, OK: 0 };
        for (const result of allResults) byStatus[result.status]++;
        return byStatus;
    }, [allResults]);

    const columns = useMemo(() => buildColumns(horizonDays), [horizonDays]);

    const table = useReactTable({
        data: filteredResults,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <Content>
            <div className='container mx-auto py-10 space-y-6'>
                <div className='flex flex-wrap items-start justify-between gap-4'>
                    <div>
                        <h1 className='text-3xl font-bold'>Buchbarkeits-Check</h1>
                        <p className='text-muted-foreground mt-1 max-w-3xl'>
                            Prüft für jedes öffentliche Bookable Item, wie viele freie Slots jede Option in den nächsten {horizonDays} Tagen im Booking Funnel hat. Jede Option wird nur an den Standorten geprüft, an denen die Variante angeboten wird. Upsell-Bundles mit aktivem Popup werden zusätzlich geprüft, jeweils nur am Standort, für den die Variante konfiguriert ist.
                            Läuft täglich um 05:00 Uhr automatisch. Die am wenigsten buchbaren Kombinationen stehen oben.
                        </p>
                    </div>
                    <Button
                        onClick={() => startRunMutation.mutate()}
                        disabled={isRunning || startRunMutation.isPending}
                    >
                        {isRunning || startRunMutation.isPending ? <Loader2 className='animate-spin' /> : <Play />}
                        {isRunning ? 'Check läuft…' : 'Jetzt prüfen'}
                    </Button>
                </div>

                {latestRunQuery.isError && (
                    <Alert variant='destructive'>
                        <TriangleAlert className='h-4 w-4' />
                        <AlertTitle>Fehler beim Laden</AlertTitle>
                        <AlertDescription>{latestRunQuery.error.message}</AlertDescription>
                    </Alert>
                )}

                {isRunning && latestRun && (
                    <Alert>
                        <Loader2 className='h-4 w-4 animate-spin' />
                        <AlertTitle>Check läuft seit {formatDateTime(latestRun.createdAt)}</AlertTitle>
                        <AlertDescription>
                            Die Tabelle zeigt bis dahin das Ergebnis des letzten abgeschlossenen Checks.
                        </AlertDescription>
                    </Alert>
                )}

                {latestRun?.status === 'FAILED' && (
                    <Alert variant='destructive'>
                        <TriangleAlert className='h-4 w-4' />
                        <AlertTitle>Letzter Check vom {formatDateTime(latestRun.createdAt)} ist fehlgeschlagen</AlertTitle>
                        <AlertDescription>{latestRun.error ?? 'Unbekannter Fehler'}</AlertDescription>
                    </Alert>
                )}

                <div className='grid grid-cols-2 md:grid-cols-5 gap-4'>
                    <Card>
                        <CardHeader className='pb-2'>
                            <CardDescription>Letzter Check</CardDescription>
                            <CardTitle className='text-base'>
                                {resultsRun ? formatDateTime(resultsRun.createdAt) : '–'}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className='text-xs text-muted-foreground'>
                            {resultsRun
                                ? `${TRIGGER_LABELS[resultsRun.trigger] ?? resultsRun.trigger}${resultsRun.finishedAt ? `, Dauer ${formatDuration(resultsRun.createdAt, resultsRun.finishedAt)}` : ''}`
                                : 'Noch kein Check abgeschlossen'}
                        </CardContent>
                    </Card>
                    {(['NONE', 'LOW', 'ERROR', 'OK'] as const).map((status) => (
                        <Card key={status}>
                            <CardHeader className='pb-2'>
                                <CardDescription>{STATUS_LABELS[status]}</CardDescription>
                                <CardTitle className={cn('text-2xl', status === 'NONE' && counts.NONE > 0 && 'text-red-700')}>
                                    {counts[status]}
                                </CardTitle>
                            </CardHeader>
                        </Card>
                    ))}
                </div>

                <div className='flex flex-wrap items-end gap-4'>
                    <div className='space-y-1'>
                        <Label>Suche</Label>
                        <Input
                            placeholder='Bookable Item oder Option'
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            className='w-64'
                        />
                    </div>
                    <div className='space-y-1'>
                        <Label>Standort</Label>
                        <Select value={storeFilter} onValueChange={setStoreFilter}>
                            <SelectTrigger className='w-56'>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>Alle Standorte</SelectItem>
                                {storeOptions.map(([value, name]) => (
                                    <SelectItem key={value} value={value}>{name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='flex items-center gap-2 pb-2'>
                        <Switch id='only-problems' checked={onlyProblems} onCheckedChange={setOnlyProblems} />
                        <Label htmlFor='only-problems'>Nur Probleme anzeigen</Label>
                    </div>
                    <p className='text-sm text-muted-foreground pb-2 ml-auto'>
                        {filteredResults.length} von {allResults.length} Kombinationen
                    </p>
                </div>

                <div className='rounded-md border'>
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {latestRunQuery.isLoading ? (
                                Array.from({ length: 6 }).map((_, index) => (
                                    <TableRow key={index}>
                                        {columns.map((_column, columnIndex) => (
                                            <TableCell key={columnIndex}><Skeleton className='h-5 w-full' /></TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : table.getRowModel().rows.length > 0 ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id} className='align-top'>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className='h-24 text-center text-muted-foreground'>
                                        {resultsRun
                                            ? 'Keine Kombinationen für die aktuellen Filter.'
                                            : 'Noch keine Ergebnisse. Starte den ersten Check mit „Jetzt prüfen“.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>

                <p className='text-xs text-muted-foreground'>
                    Ein Slot ist eine mögliche Startzeit im Booking Funnel. Mehrere Slots können zum selben freien Zeitfenster gehören, daher ist „Buchbare Tage“ meist aussagekräftiger.
                    „Behandler“ zeigt, auf wie viele Behandler:innen sich die freien Slots verteilen. Weicht die Zahl von der erwarteten ab, fehlen z. B. Schichten oder Freigaben.
                    Geräte-Defekte werden unter <Link to='/equipment' className='underline'>Geräte</Link> gepflegt, Sichtbarkeit im Funnel im <Link to='/cms/booking-funnel' className='underline'>CMS</Link>.
                </p>
            </div>
        </Content>
    );
};

export default BookabilityCheck;
